import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import { motion } from "motion/react";
import { X, ZoomIn, ZoomOut, RotateCw, Check, Loader2 } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  const outputCanvas = document.createElement("canvas");
  const outputSize = 512;
  outputCanvas.width = outputSize;
  outputCanvas.height = outputSize;
  const outputCtx = outputCanvas.getContext("2d");

  outputCtx.drawImage(canvas, 0, 0, pixelCrop.width, pixelCrop.height, 0, 0, outputSize, outputSize);

  return new Promise((resolve) => {
    outputCanvas.toBlob(
      (blob) => resolve(blob),
      "image/jpeg",
      0.9
    );
  });
}

export default function AvatarCropper({ imageSrc, onCropDone, onCancel, uploading }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.style.pointerEvents = "none";
    }
    document.body.style.overflow = "hidden";

    return () => {
      if (root) {
        root.style.pointerEvents = "";
      }
      document.body.style.overflow = "";
    };
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
    onCropDone(croppedBlob);
  };

  const modalContent = (
    <motion.div
      id="avatar-cropper_modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      style={{ pointerEvents: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget && !uploading) onCancel(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg bg-neutral-900 border border-neutral-800 rounded-md overflow-hidden"
        style={{ pointerEvents: "auto" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
          <h3 className="text-xl font-medium text-white">Настройка фото</h3>
          <button
            onClick={onCancel}
            disabled={uploading}
            className="p-2 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative w-full aspect-square bg-neutral-950">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center gap-4">
            <ZoomOut className="w-5 h-5 text-neutral-500 flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full appearance-none bg-neutral-700 accent-primary-500 cursor-pointer"
            />
            <ZoomIn className="w-5 h-5 text-neutral-500 flex-shrink-0" />
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-lg font-extralight transition-colors disabled:opacity-50"
            >
              <RotateCw className="w-4 h-4" />
              Повернуть
            </button>

            <div className="flex gap-3">
              <button
                onClick={onCancel}
                disabled={uploading}
                className="px-5 py-2 rounded-md border border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white text-lg font-extralight transition-colors disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleConfirm}
                disabled={uploading}
                className="flex items-center gap-2 px-5 py-2 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-medium transition-colors disabled:opacity-60"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Загрузка...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Сохранить
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return createPortal(modalContent, document.body);
}
